import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { useAuth } from '../../context/AuthContext';
import { canAccess } from '../../constants/tiers';
import { toast } from 'sonner';
import { Search, Loader2, MapPin, Plane, ChevronLeft, Lock, UsersRound } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Peer Circles → Member Directory (iter 73).
 *
 * Gated to Community + Connected. Search by name / city. Members who have
 * disabled "Make my profile searchable" never appear here.
 *
 * The Connect button is a Phase-1 placeholder - Phase 2 ships contact
 * requests + DM unlock. We render the button so the surface is complete,
 * and toast a "coming soon" notice on click.
 */
const InitialBubble = ({ name, size = 56 }) => {
  const initial = (name || '?').trim()[0]?.toUpperCase() || '?';
  return (
    <div
      className="rounded-full bg-[#2CA8A0] text-white font-semibold flex items-center justify-center shrink-0"
      style={{ width: size, height: size, fontSize: size / 2.5 }}
    >
      {initial}
    </div>
  );
};

const MemberCard = ({ m, onConnect, busy }) => {
  const displayName = `${m.first_name}${m.last_name_initial ? ` ${m.last_name_initial}.` : ''}`;
  const from = [m.from_city, m.from_country].filter(Boolean).join(', ');
  const status = m.connection_status || 'none';

  // iter 120 - Messaging is now open by default. The primary action on
  // a member card is "Message" (unless the member has opted out via the
  // Allow-direct-messages toggle, in which case we fall back to the
  // existing Connect flow). Connection requests still work via the
  // dedicated /portal/peer-circles/invite page if a member wants them.
  //   accepted   → "Message"     (existing thread)
  //   pending out→ "Pending"
  //   pending in → "Respond"
  //   else + DMs allowed → "Message" (open a new thread; no request needed)
  //   else + DMs closed  → "Connect" (legacy fallback so the card isn't dead)
  let btnLabel = 'Connect';
  let btnDisabled = false;
  let btnAction = 'request';
  if (status === 'pending' && m.connection_direction === 'outgoing') {
    btnLabel = 'Pending'; btnDisabled = true; btnAction = 'noop';
  } else if (status === 'pending' && m.connection_direction === 'incoming') {
    btnLabel = 'Respond'; btnAction = 'open-requests';
  } else if (status === 'accepted') {
    btnLabel = 'Message'; btnAction = 'open-thread';
  } else if (m.allow_direct_messages !== false) {
    btnLabel = 'Message'; btnAction = 'open-thread';
  }

  return (
    <div
      className="bg-white border border-[#EFE6CF] rounded-2xl p-5 flex flex-col gap-4 hover:border-[#2CA8A0]/50 transition"
      data-testid={`member-card-${m.user_id}`}
    >
      <div className="flex items-start gap-3">
        <Link to={`/portal/members/${m.user_id}`} className="shrink-0">
          {m.profile_photo_url ? (
            <img
              src={m.profile_photo_url}
              alt=""
              className="w-14 h-14 rounded-full object-cover ring-2 ring-[#EFE6CF]"
            />
          ) : (
            <InitialBubble name={m.first_name} size={56} />
          )}
        </Link>
        <div className="flex-1 min-w-0">
          <Link
            to={`/portal/members/${m.user_id}`}
            className="font-display italic text-lg text-[#0A1F2E] hover:text-[#1A5C6E] block truncate"
            data-testid={`member-card-name-${m.user_id}`}
          >
            {displayName}
          </Link>
          {from && (
            <p className="text-xs text-[#0A1F2E]/55 mt-0.5 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {from}
            </p>
          )}
          {m.destination_city && (
            <p className="text-xs text-[#1A5C6E] mt-0.5 flex items-center gap-1">
              <Plane className="w-3 h-3" /> Moving to {m.destination_city}
            </p>
          )}
        </div>
      </div>
      {m.bio && (
        <p className="text-sm text-[#0A1F2E]/70 leading-snug line-clamp-3">{m.bio}</p>
      )}
      <button
        type="button"
        onClick={() => onConnect(m, btnAction)}
        disabled={btnDisabled || busy}
        className={`self-start font-mono uppercase text-[10px] tracking-[0.15em] px-4 py-2 rounded-full transition ${
          btnDisabled
            ? 'bg-[#EFE6CF] text-[#0A1F2E]/45 cursor-not-allowed'
            : 'bg-[#0A1F2E] hover:bg-[#163448] text-white'
        }`}
        data-testid={`member-card-connect-${m.user_id}`}
      >
        {busy ? '…' : btnLabel}
      </button>
    </div>
  );
};

const PortalPeerCirclesMembers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const unlocked = canAccess(user?.role, 'peerCircles');

  const [q, setQ] = useState('');
  const [city, setCity] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchMembers = useCallback(async () => {
    if (!unlocked) {
      // User isn't a Peer Circles tier yet - render the locked upgrade card.
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (city.trim()) params.set('city', city.trim());
      const url = `${API_URL}/api/peer-circles/members${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await fetch(url, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail?.message || d.detail || 'Could not load members');
      }
      const data = await res.json();
      setMembers(data.members || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [q, city, unlocked]);

  // Re-fetch when the auth-derived `unlocked` flag flips true (user loaded).
  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked]);

  const handleConnect = async (m, action) => {
    if (action === 'open-requests') {
      navigate('/portal/peer-circles/inbox?tab=requests');
      return;
    }
    if (action === 'open-thread') {
      navigate(`/portal/peer-circles/inbox?to=${m.user_id}`);
      return;
    }
    // action === 'request' - POST a new connection request
    setBusyId(m.user_id);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/peer-circles/connections/request`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ recipient_id: m.user_id }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail?.message || d.detail || 'Could not send request');
      }
      toast.success(`Connect request sent to ${m.first_name}.`);
      // Optimistically flip the card to "Pending / outgoing"
      setMembers((cur) =>
        cur.map((x) =>
          x.user_id === m.user_id
            ? { ...x, connection_status: 'pending', connection_direction: 'outgoing' }
            : x
        ),
      );
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMembers();
  };

  if (!unlocked) {
    return (
      <PortalLayout activeTab="peerCircles">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-14" data-testid="members-locked">
          <div className="bg-white border border-[#EFE6CF] rounded-2xl p-8 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#F4ECD8] flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-[#1A5C6E]" />
            </div>
            <h1 className="font-display italic text-3xl text-[#0A1F2E] mb-2">Member Directory is locked</h1>
            <p className="text-[#0A1F2E]/70 mb-6">Browse and meet other Mundalo movers with a Community or Connected plan.</p>
            <Link
              to="/portal/upgrade"
              className="inline-flex items-center gap-1 bg-[#0A1F2E] hover:bg-[#163448] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-6 py-3 rounded-full transition"
              data-testid="members-upgrade-cta"
            >
              See plans
            </Link>
          </div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout activeTab="peerCircles">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-10 md:py-14" data-testid="members-directory">
        <button
          type="button"
          onClick={() => navigate('/portal/peer-circles')}
          className="inline-flex items-center gap-1 text-[#1A5C6E] hover:text-[#0A1F2E] text-sm mb-6 transition font-mono uppercase tracking-[0.12em] text-[11px]"
          data-testid="members-back-to-hub"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Peer Circles
        </button>

        <header className="mb-8">
          <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">
            Peer Circles · Members
          </p>
          <h1 className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-3">
            Mundalo movers.
          </h1>
          <p className="font-body text-base md:text-lg text-[#0A1F2E]/75 max-w-2xl">
            Search Mundalo members by name or city. Members who hide their profile won&rsquo;t appear here.
          </p>
        </header>

        <form
          onSubmit={handleSearch}
          className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 mb-8"
          data-testid="members-search-form"
        >
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#0A1F2E]/40" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name…"
              className="w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-full pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/20"
              data-testid="members-search-name"
            />
          </div>
          <div className="relative">
            <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#0A1F2E]/40" />
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Search by city…"
              className="w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-full pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/20"
              data-testid="members-search-city"
            />
          </div>
          <button
            type="submit"
            className="bg-[#0A1F2E] hover:bg-[#163448] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-6 py-2.5 rounded-full transition"
            data-testid="members-search-submit"
          >
            Search
          </button>
        </form>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" />
          </div>
        )}

        {!loading && error && (
          <div className="bg-[#B91C1C]/10 border border-[#B91C1C]/30 text-[#B91C1C] p-4 mb-6 rounded-xl text-sm" data-testid="members-error">
            {error}
          </div>
        )}

        {!loading && !error && members.length === 0 && (
          <div
            className="bg-white border border-[#EFE6CF] rounded-2xl p-12 text-center"
            data-testid="members-empty"
          >
            <UsersRound className="w-12 h-12 text-[#1A5C6E]/40 mx-auto mb-3" />
            <p className="text-[#0A1F2E]/65">No members match your search.</p>
          </div>
        )}

        {!loading && !error && members.length > 0 && (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
            data-testid="members-grid"
          >
            {members.map((m) => (
              <MemberCard key={m.user_id} m={m} onConnect={handleConnect} busy={busyId === m.user_id} />
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
};

export default PortalPeerCirclesMembers;
